import Card from "@/components/common/Card";
import { API_BASE_URL } from "@/lib/axios";
import { 
  BookOpenIcon, 
  CheckCircleIcon, 
  PlayIcon,
  CalendarIcon,
  ChartBarIcon
} from "@heroicons/react/24/outline";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

interface LearningHistoryTabProps {
  user: any;
}

const LearningHistoryTab = ({ user }: LearningHistoryTabProps) => {
  const { t } = useTranslation();

  const learningProgress = user?.learningProgress || [];
  const examResults = user?.examResults || [];


  return (
    <Card className="backdrop-blur-sm bg-white/80 dark:bg-gray-800/80 border-0 shadow-xl hover:shadow-2xl transition-all duration-300">
      <div className="p-6">
        <h3 className="mb-6 text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent dark:from-white dark:to-gray-300">
          {t("profile.learningHistory")}
        </h3>

        <div className="space-y-8">
          {/* Learning Progress Section */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <BookOpenIcon className="h-6 w-6 text-blue-600" />
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t("profile.courseProgress")}
              </h4>
              <span className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full text-sm font-medium">
                {learningProgress.length}
              </span>
            </div>

            {learningProgress.length > 0 ? (
              <div className="space-y-4">
                {learningProgress.map((progress: any, index: number) => (
                  <motion.div
                    key={progress._id || index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg p-6 border border-blue-200 dark:border-blue-700"
                  >
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className="h-12 w-12 rounded-lg bg-blue-600 flex items-center justify-center">
                          <BookOpenIcon className="h-6 w-6 text-white" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="text-lg font-medium text-gray-900 dark:text-white">
                            Course ID: {progress.courseId}
                          </h5>
                          <span className="bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-2 py-1 rounded-full text-sm font-medium">
                            {progress.progress}% Complete
                          </span>
                        </div>
                        
                        <div className="mb-4">
                          <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 mb-1">
                            <span>{t("profile.progress")}</span>
                            <span>{progress.progress}%</span>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${progress.progress}%` }}
                            ></div>
                          </div>
                        </div>

                        {progress.startedAt && (
                          <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400 mb-2">
                            <CalendarIcon className="h-4 w-4" />
                            <span>
                              {t("profile.startedOn")}: {new Date(progress.startedAt).toLocaleDateString()}
                            </span>
                          </div>
                        )}

                        {/* Tutorials Progress */}
                        {progress.tutorials && progress.tutorials.length > 0 && (
                          <div className="mt-4">
                            <h6 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                              {t("profile.tutorials")} ({progress.tutorials.length})
                            </h6>
                            <div className="space-y-2">
                              {progress.tutorials.map((tutorial: any, tutorialIndex: number) => (
                                <div key={tutorial._id || tutorialIndex} className="flex items-center justify-between text-sm">
                                  <div className="flex items-center space-x-2">
                                    {tutorial.isCompleted ? (
                                      <CheckCircleIcon className="h-4 w-4 text-green-500" />
                                    ) : (
                                      <PlayIcon className="h-4 w-4 text-blue-500" />
                                    )}
                                    <span className="text-gray-700 dark:text-gray-300">
                                      Tutorial {tutorialIndex + 1}
                                    </span>
                                  </div>
                                  <span className="text-gray-600 dark:text-gray-400">
                                    {tutorial.progress}%
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Quiz Results */}
                        {progress.quizzes && progress.quizzes.length > 0 && (
                          <div className="mt-4">
                            <h6 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                              {t("profile.quizzes")} ({progress.quizzes.length})
                            </h6>
                            <div className="space-y-2">
                              {progress.quizzes.map((quiz: any, quizIndex: number) => (
                                <div key={quiz._id || quizIndex} className="flex items-center justify-between text-sm">
                                  <div className="flex items-center space-x-2">
                                    <ChartBarIcon className="h-4 w-4 text-purple-500" />
                                    <span className="text-gray-700 dark:text-gray-300">
                                      Quiz {quizIndex + 1}
                                    </span>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                      quiz.isPassed 
                                        ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                                        : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                                    }`}>
                                      {quiz.isPassed ? t("profile.passed") : t("profile.failed")}
                                    </span>
                                    <span className="text-gray-600 dark:text-gray-400">
                                      {quiz.score || 0}%
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <BookOpenIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400">
                  {t("profile.noLearningProgress")}
                </p>
              </div>
            )}
          </div>

          {/* Exam Results Section */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <ChartBarIcon className="h-6 w-6 text-purple-600" />
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t("profile.examResults")}
              </h4>
              <span className="bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 px-2 py-1 rounded-full text-sm font-medium">
                {examResults.length}
              </span>
            </div>

            {examResults.length > 0 ? (
              <div className="grid gap-4 md:grid-cols-2">
                {examResults.map((exam: any, index: number) => (
                  <motion.div
                    key={exam._id || index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="bg-gradient-to-r from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-lg p-4 border border-purple-200 dark:border-purple-700"
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 rounded-full bg-purple-600 flex items-center justify-center">
                          <ChartBarIcon className="h-5 w-5 text-white" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h5 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {exam.examName || exam.title || t("profile.unnamedExam")}
                        </h5>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          Score: {exam.score || 0}%
                        </p>
                        {exam.completedAt && (
                          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                            {new Date(exam.completedAt).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400">
                  {t("profile.noExamResults")}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default LearningHistoryTab;
